import React, { useState, useEffect, useRef } from "react";
import { getSupabase } from "../../../components/supabaseClient";
import {
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  validateSupabaseStorageUrl,
  checkAudioAccessibility,
  getAudioErrorMessage,
  extractAudioMetadata,
} from "../../../utils/audioUtils";

/** Shown when file exists but has no playable audio (e.g. recording failed to save). */
const EMPTY_RECORDING_ERROR_MSG =
  "File exists but contains no playable audio. The recording may not have been saved properly.";

/** A stored Supabase storage link (public or signed): never playable as-is. */
const STORAGE_URL_RE = /^https:\/\/[^/]+\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/;

/**
 * Surface colors for the error panel, per host `tone`.
 *
 * The light column is exactly what this component always used, so the Chakra
 * modals that host it (Audio Manager, Audio Player) render byte-identically.
 * The dark column exists for the clinic Audios modal: Chakra's `<c>.50` tints
 * are near-white, so on a dark panel they glare, and their text — which
 * inherits from the host — washes out on top of them.
 */
const SURFACES = {
  light: {
    red:    { bg: "red.50",    border: "red.200",    title: "red.700",    body: "red.600" },
    orange: { bg: "orange.50", border: "orange.200", title: "orange.700", body: "orange.600" },
    green:  { bg: "green.50",  border: "green.200",  title: "green.700",  body: "green.600" },
    yellow: { bg: "yellow.50", border: "yellow.200", title: "yellow.700", body: "yellow.600" },
    gray:   { bg: "gray.50",   border: "gray.200",   title: "gray.700",   body: "gray.600" },
    info:   "blue.500",
    ok:     "green.500",
    bad:    "red.500",
  },
  dark: {
    red:    { bg: "rgba(229, 62, 62, 0.16)",  border: "rgba(254, 178, 178, 0.28)", title: "red.200",    body: "red.100" },
    orange: { bg: "rgba(221, 107, 32, 0.16)", border: "rgba(251, 211, 141, 0.28)", title: "orange.200", body: "orange.100" },
    green:  { bg: "rgba(56, 161, 105, 0.16)", border: "rgba(154, 230, 180, 0.28)", title: "green.200",  body: "green.100" },
    yellow: { bg: "rgba(214, 158, 46, 0.16)", border: "rgba(250, 240, 137, 0.28)", title: "yellow.200", body: "yellow.100" },
    gray:   { bg: "whiteAlpha.100",           border: "whiteAlpha.300",            title: "gray.100",   body: "gray.300" },
    info:   "blue.300",
    ok:     "green.300",
    bad:    "red.300",
  },
};

const AudioPlayer = ({
  audioUrl,
  scheId,
  audio_duration,
  patientId,
  audioSavedAt,
  /** Shown above transport controls when set (friendly / storage filename). */
  audioDisplayName,
  /**
   * Palette of the surface this player is rendered on: "light" (every Chakra
   * modal that hosts it) or "dark" (the clinic Audios modal in dark mode).
   * Only affects the error panel's tinted boxes — the normal player inherits
   * its text color from the host either way.
   */
  tone = "light",
}) => {
  const surf = SURFACES[tone] || SURFACES.light;
  // Chakra's Alert bakes in a `<c>.100` subtle background and lets its text
  // inherit; both are wrong on a dark host, so override the pair explicitly.
  const alertTone = (hue) =>
    tone === "dark" ? { bg: surf[hue].bg, color: surf[hue].body } : {};
  const [duration, setDuration] = useState(audio_duration); // Total duration of the audio
  const [currentTime, setCurrentTime] = useState(0); // Current playback time
  const [isPlaying, setIsPlaying] = useState(false); // Playback state
  const [audioError, setAudioError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [urlValidation, setUrlValidation] = useState(null);
  const [audioMetadata, setAudioMetadata] = useState(null);
  const [accessibilityCheck, setAccessibilityCheck] = useState(null);
  const [isCheckingAccessibility, setIsCheckingAccessibility] = useState(false);
  const [backupUrl, setBackupUrl] = useState(null);
  const [allBackupsFailed, setAllBackupsFailed] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState(null);
  const [replaceSuccess, setReplaceSuccess] = useState(false);
  const [autoDeleteStatus, setAutoDeleteStatus] = useState(null);
  // Storage links are re-signed before playback: the buckets are private, so
  // the stored /public/ link always fails, and a stored /sign/ link's token
  // expires (mobile uploads used to persist a 1-hour backup-audio link).
  // Until the fresh link exists there is no <audio> element at all -- handing
  // it the raw link raced the signing (slow while RDS owns the bucket: RDS is
  // asked first) and its bogus "format error" could auto-delete a good backup.
  // `resolved.for` is the audioUrl the result belongs to, so a stale result
  // never plays for the next recording.
  const [resolved, setResolved] = useState({ for: null, url: null });
  const needsSigning = typeof audioUrl === "string" && STORAGE_URL_RE.test(audioUrl);
  const isSigning = needsSigning && resolved.for !== audioUrl;
  const resolvedAudioUrl = needsSigning ? (isSigning ? null : resolved.url) : audioUrl;

  useEffect(() => {
    let active = true;
    const storageMatch = typeof audioUrl === "string" ? audioUrl.match(STORAGE_URL_RE) : null;
    if (!storageMatch) return undefined;

    const signFailed = (message) => {
      if (!active) return;
      setResolved({ for: audioUrl, url: null });
      setErrorMessage(`Could not create a secure link for this recording: ${message}`);
      setAudioError(true);
    };

    (async () => {
      const bucket = storageMatch[1];
      const filePath = decodeURIComponent(storageMatch[2].split("?")[0]);
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(filePath, 7200); // 2 hours

        if (error || !data?.signedUrl) {
          console.warn("⚠️ [AudioPlayer] Failed to create signed URL:", error?.message);
          signFailed(error?.message || "no link returned");
        } else {
          console.log("✅ [AudioPlayer] Resolved signed URL for private bucket playback");
          if (active) setResolved({ for: audioUrl, url: data.signedUrl });
        }
      } catch (err) {
        console.error("❌ [AudioPlayer] Error resolving signed URL:", err);
        signFailed(err?.message || "unexpected error");
      }
    })();

    return () => {
      active = false;
    };
  }, [audioUrl]);

  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const accessibilityCheckRef = useRef(null);
  const lastMediaErrorCodeRef = useRef(null);
  const autoDeletedUrlRef = useRef(new Set());
  const probedDurationRef = useRef(new Set());

  const autoDeleteBackupUrl = async (targetUrl) => {
    if (!targetUrl || autoDeletedUrlRef.current.has(targetUrl)) return false;
    const storageMatch = targetUrl.match(
      /^https:\/\/[^/]+\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/,
    );
    if (!storageMatch) return false;

    const bucket = storageMatch[1];
    const filePath = storageMatch[2].split("?")[0];
    if (bucket !== "backup-audio" || !filePath) return false;

    autoDeletedUrlRef.current.add(targetUrl);
    try {
      const { BASE_URLS } = await import("../../../components/env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/transcription-backup/delete-file/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bucket,
            file_path: filePath,
            patient_id: patientId,
          }),
        },
      );
      if (!response.ok) return false;

      window.dispatchEvent(
        new CustomEvent("audioBackupAutoDeleted", {
          detail: {
            patientId,
            audioUrl: targetUrl,
            bucket,
            filePath,
          },
        }),
      );
      return true;
    } catch (err) {
      console.error("❌ [AudioPlayer] Auto-delete backup failed:", err);
      return false;
    }
  };

  // Update duration when audio_duration prop changes
  useEffect(() => {
    // Validate the duration before setting it
    if (audio_duration && isFinite(audio_duration) && audio_duration > 0) {
      setDuration(audio_duration);
      console.log("✅ Setting duration from prop:", audio_duration);
    } else {
      console.warn("⚠️ Invalid duration from prop:", audio_duration);
      // Don't set invalid durations, let the audio metadata handle it
    }
    setCurrentTime(0); // Reset current time when switching audio files
    setIsPlaying(false); // Reset play state when switching audio files
  }, [audio_duration]);

  // Validate audio URL and check accessibility
  useEffect(() => {
    console.log("🎵 Validating audio URL:", audioUrl);

    // Reset states
    setAudioError(false);
    setErrorMessage("");
    setIsValidUrl(false);
    setUrlValidation(null);
    setAudioMetadata(null);
    setAccessibilityCheck(null);
    setBackupUrl(null);
    setAllBackupsFailed(false);
    setIsReplacing(false);
    setReplaceError(null);
    setReplaceSuccess(false);
    setAutoDeleteStatus(null);
    accessibilityCheckRef.current = null;
    lastMediaErrorCodeRef.current = null;

    if (!audioUrl || typeof audioUrl !== "string" || audioUrl.trim() === "") {
      console.warn("⚠️ Invalid audio URL: Empty or null");
      setErrorMessage("No audio file provided");
      return;
    }

    // Check for valid URL patterns
    const urlPattern = /^https?:\/\/.+/;
    const dataUrlPattern = /^data:audio\//;

    if (!urlPattern.test(audioUrl) && !dataUrlPattern.test(audioUrl)) {
      console.warn("⚠️ Invalid audio URL format:", audioUrl);
      setErrorMessage("Invalid audio URL format");
      return;
    }

    // Validate Supabase storage URL if applicable
    const validation = validateSupabaseStorageUrl(audioUrl);
    setUrlValidation(validation);

    if (!validation.isValid) {
      console.warn(
        "⚠️ Supabase storage URL validation failed:",
        validation.error
      );
      setErrorMessage(validation.error);
      return;
    }

    // Extract metadata
    const metadata = extractAudioMetadata(audioUrl);
    setAudioMetadata(metadata.metadata);

    setIsValidUrl(true);
    console.log("✅ Audio URL validation passed");

    // Check accessibility of the playable URL. A storage link has none until
    // it is re-signed (resolvedAudioUrl is null meanwhile); an RDS blob: URL
    // is local, so there is nothing to check.
    if (resolvedAudioUrl && urlPattern.test(resolvedAudioUrl)) {
      setIsCheckingAccessibility(true);
      checkAudioAccessibility(resolvedAudioUrl)
        .then((accessibility) => {
          console.log("🔍 Accessibility check result:", accessibility);
          accessibilityCheckRef.current = accessibility;
          setAccessibilityCheck(accessibility);

          if (!accessibility.isAccessible) {
            const errorMsg = getAudioErrorMessage(
              resolvedAudioUrl,
              validation,
              accessibility
            );
            setErrorMessage(errorMsg);
            setAudioError(true);
            // Backup lookup is handled by the useEffect that watches audioError
          } else if (
            accessibility.isAccessible &&
            (lastMediaErrorCodeRef.current === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
              lastMediaErrorCodeRef.current === MediaError.MEDIA_ERR_DECODE)
          ) {
            lastMediaErrorCodeRef.current = null;
            setErrorMessage(EMPTY_RECORDING_ERROR_MSG);
          }
        })
        .catch((error) => {
          console.warn("⚠️ Accessibility check failed:", error);
          setAccessibilityCheck({
            isAccessible: false,
            status: null,
            error: error.message || "Accessibility check failed",
          });
        })
        .finally(() => {
          setIsCheckingAccessibility(false);
        });
    }
  }, [audioUrl, resolvedAudioUrl]);

  useEffect(() => {
    const maybeAutoDeleteCorruptBackup = async () => {
      if (!audioError || !audioUrl || !errorMessage) return;
      // Only a failure of the freshly signed link proves the file is bad; a
      // failed signing (network, auth, RDS hiccup) says nothing about it.
      if (!resolvedAudioUrl) return;
      if (autoDeletedUrlRef.current.has(audioUrl)) return;

      const storageMatch = audioUrl.match(
        /^https:\/\/[^/]+\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/,
      );
      if (!storageMatch) return;

      const bucket = storageMatch[1];
      const filePath = storageMatch[2].split("?")[0];
      if (bucket !== "backup-audio" || !filePath) return;

      const corruptByStatus = accessibilityCheck?.status === 400;
      const corruptByMessage =
        errorMessage === EMPTY_RECORDING_ERROR_MSG ||
        /corrupt|unsupported format|not supported|no playable audio/i.test(
          errorMessage,
        );

      if (!corruptByStatus && !corruptByMessage) return;

      setAutoDeleteStatus("deleting");

      try {
        const deleted = await autoDeleteBackupUrl(audioUrl);
        if (!deleted) throw new Error("Delete failed");
        setAutoDeleteStatus("deleted");
      } catch (err) {
        console.error("❌ [AudioPlayer] Auto-delete corrupt backup failed:", err);
        setAutoDeleteStatus("failed");
      }
    };

    maybeAutoDeleteCorruptBackup();
  }, [audioError, audioUrl, resolvedAudioUrl, errorMessage, accessibilityCheck, patientId]);

  // When playback fails, find a valid replacement backup directly from storage.
  // We intentionally avoid localStorage backup URLs here because stale entries
  // create noisy repeated 400s in the browser console.
  useEffect(() => {
    if (!audioError || backupUrl || !audioUrl) return;

    // Returns true if the browser can actually decode and play the file.
    // WebM files always have duration=Infinity in metadata, so duration is NOT
    // a reliable check. Instead we wait for 'canplay' (browser decoded enough
    // data to start playback) vs 'error' (corrupt / empty file).
    // Returns true if the browser can decode and play the file (canplay fires).
    // We do NOT use duration — WebM always reports Infinity in metadata headers.
    const isAudioPlayable = (url) => new Promise((resolve) => {
      const tmp = document.createElement("audio");
      const cleanup = () => { try { tmp.src = ""; } catch (_) {} };
      const timer = setTimeout(() => { cleanup(); resolve(false); }, 10000);
      tmp.addEventListener("canplay", () => { clearTimeout(timer); cleanup(); resolve(true); });
      tmp.addEventListener("error",   () => { clearTimeout(timer); cleanup(); resolve(false); });
      tmp.preload = "auto";
      tmp.src = url;
    });

    const findBackup = async () => {
      // Extract the audio sequence number from the filename (e.g. "Audio5" → 5).
      // Backups are only considered if their number matches (e.g. "Backup5_*"),
      // ensuring we never show a backup from a different recording session.
      const audioNumMatch = audioUrl.match(/_Audio(\d+)\./i);
      const audioNum = audioNumMatch ? audioNumMatch[1] : null;
      const backupPrefix = audioNum ? `Backup${audioNum}_` : "Backup";

      const candidates = [];

      // Query backup-audio bucket directly and only test currently listed objects.
      const urlMatch = audioUrl.split("?")[0].match(/^(https:\/\/[^/]+)\/storage\/v1\/object\/(?:public|sign)\/[^/]+\/(.+)\/[^/]+$/);
      if (urlMatch) {
        const supabaseBase = urlMatch[1];
        const folderPath = decodeURIComponent(urlMatch[2]);
        try {
          const supabase = await getSupabase();
          const { data: files, error } = await supabase.storage
            .from("backup-audio")
            .list(folderPath, { limit: 100, sortBy: { column: "created_at", order: "desc" } });

          if (error) {
            console.warn("⚠️ [AudioPlayer] Supabase backup list error:", error.message);
          } else {
            const cutoff = audioSavedAt ? new Date(audioSavedAt) : null;
            const matchedFiles = (files || [])
              .filter(f => f.name.includes(backupPrefix))
              .filter(f => !cutoff || !f.created_at || new Date(f.created_at) <= cutoff)
              .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

            for (const f of matchedFiles) {
              try {
                const { data: signedData, error: signError } = await supabase.storage
                  .from("backup-audio")
                  .createSignedUrl(`${folderPath}/${f.name}`, 7200);

                if (signError) {
                  console.warn("⚠️ [AudioPlayer] Failed to sign backup candidate:", signError.message);
                } else if (signedData?.signedUrl) {
                  if (!candidates.some(c => c.url === signedData.signedUrl)) {
                    candidates.push({ url: signedData.signedUrl, label: f.name });
                  }
                }
              } catch (signErr) {
                console.error("❌ [AudioPlayer] Error signing backup candidate:", signErr);
              }
            }
          }
        } catch (err) {
          console.error("❌ [AudioPlayer] Error querying backup storage:", err);
        }
      }

      if (candidates.length === 0) {
        setAllBackupsFailed(true);
        return;
      }

      // Validate each candidate (most recent first), use the first playable one.
      for (const candidate of candidates) {
        const access = await checkAudioAccessibility(candidate.url);
        if (!access?.isAccessible && access?.status === 400) {
          await autoDeleteBackupUrl(candidate.url);
          continue;
        }
        const playable = await isAudioPlayable(candidate.url);
        if (playable) {
          setBackupUrl(candidate.url);
          return;
        }
        await autoDeleteBackupUrl(candidate.url);
      }

      // All candidates tried and none were playable
      setAllBackupsFailed(true);
    };

    findBackup();
  }, [audioError]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReplaceWithBackup = async () => {
    if (!backupUrl || !audioUrl) return;
    setIsReplacing(true);
    setReplaceError(null);

    try {
      // Parse original audio URL → bucket + path
      const origMatch = audioUrl.match(/^https:\/\/[^/]+\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (!origMatch) throw new Error("Could not parse original audio URL");
      const origBucket = origMatch[1];
      const origPath = origMatch[2].split("?")[0];

      // Parse backup URL → bucket + path
      const backupMatch = backupUrl.match(/^https:\/\/[^/]+\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (!backupMatch) throw new Error("Could not parse backup URL");
      const backupBucket = backupMatch[1];
      const backupPath = backupMatch[2].split("?")[0];

      // Download backup file
      const response = await fetch(backupUrl);
      if (!response.ok) throw new Error(`Failed to download backup: ${response.status}`);
      const blob = await response.blob();

      const supabase = await getSupabase();

      // Upload to original location (overwrites the broken file)
      const { error: uploadError } = await supabase.storage
        .from(origBucket)
        .upload(origPath, blob, { upsert: true, contentType: "audio/webm" });
      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      // Delete backup file
      const { error: deleteError } = await supabase.storage
        .from(backupBucket)
        .remove([backupPath]);
      if (deleteError) console.warn("⚠️ Could not delete backup after replace:", deleteError.message);

      setReplaceSuccess(true);
    } catch (err) {
      console.error("❌ Replace with backup failed:", err);
      setReplaceError(err.message);
    } finally {
      setIsReplacing(false);
    }
  };

  const updateCurrentTime = () => {
    setCurrentTime(audioRef.current.currentTime);
  };

  const updateLoadedMetadata = () => {
    // If duration isn't set from Supabase, use the audio's metadata
    if (!duration || duration === 0 || isNaN(duration)) {
      const audioDuration = audioRef.current.duration;
      console.log("🎵 Audio element duration:", audioDuration);
      if (
        audioDuration &&
        !isNaN(audioDuration) &&
        isFinite(audioDuration) &&
        audioDuration > 0
      ) {
        setDuration(audioDuration);
        console.log("✅ Updated duration from audio metadata:", audioDuration);
      } else {
        // WebM from MediaRecorder often reports Infinity until the file is
        // fully decoded. Defer to the Web Audio API probe for the real value
        // — leave the display at 00:00 until it resolves.
        console.warn(
          "⚠️ Audio element duration not usable (", audioDuration, ") — probing via Web Audio API",
        );
        probeAudioForRealDuration(audioRef.current.src);
      }
    } else {
      console.log("🎵 Using duration from prop:", duration);
    }
  };

  // WebM recordings often report duration=Infinity in the audio element's
  // metadata. Use Web Audio API to decode the file once and obtain the real
  // length so the time display is correct.
  const probeAudioForRealDuration = async (srcUrl) => {
    if (!srcUrl) return;
    if (probedDurationRef.current.has(srcUrl)) return;
    probedDurationRef.current.add(srcUrl);
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      const audioCtx = new Ctor();
      const res = await fetch(srcUrl);
      if (!res.ok) {
        await audioCtx.close();
        return;
      }
      const buf = await res.arrayBuffer();
      const decoded = await audioCtx.decodeAudioData(buf.slice(0));
      if (
        decoded &&
        typeof decoded.duration === "number" &&
        isFinite(decoded.duration) &&
        decoded.duration > 0
      ) {
        setDuration(decoded.duration);
        console.log(
          "✅ [AudioPlayer] Resolved real duration via Web Audio API:",
          decoded.duration,
        );
      }
      await audioCtx.close();
    } catch (err) {
      console.warn("⚠️ [AudioPlayer] Web Audio probe failed:", err?.message || err);
    }
  };

  const handleAudioError = (e) => {
    console.error("❌ Audio loading error:", e);
    console.error("❌ Audio element:", audioRef.current);
    console.error("❌ Audio error details:", audioRef.current?.error);

    const error = audioRef.current?.error;
    let errorMsg = "Unknown audio error";
    const accessibility = accessibilityCheckRef.current;

    // Use accessibility check (ref so we have latest even if error fired before state updated) and URL validation
    if (urlValidation && !urlValidation.isValid) {
      errorMsg = urlValidation.error;
    } else if (accessibility && !accessibility.isAccessible) {
      errorMsg = getAudioErrorMessage(
        audioUrl,
        urlValidation,
        accessibility
      );
    } else if (
      accessibility?.isAccessible &&
      (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
        error?.code === MediaError.MEDIA_ERR_DECODE)
    ) {
      lastMediaErrorCodeRef.current = null;
      // File was reachable (200) but browser couldn't decode/play it → likely empty or corrupt recording
      errorMsg = EMPTY_RECORDING_ERROR_MSG;
    } else if (error) {
      lastMediaErrorCodeRef.current = error.code;
      // Fallback to standard MediaError handling
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMsg = "Audio loading was aborted";
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMsg = "Network error occurred while loading audio";
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMsg = "Audio file is corrupted or in an unsupported format";
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMsg = "Audio format is not supported or file not found";
          break;
        default:
          errorMsg = `Audio error (code: ${error.code})`;
      }
    } else {
      lastMediaErrorCodeRef.current = null;
    }

    console.error("❌ Error playing audio:", errorMsg);
    setErrorMessage(errorMsg);
    setAudioError(true);
    // Backup lookup is handled by the useEffect that watches audioError
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    console.log("🎵 Audio URL:", audioUrl);
    console.log("🎵 Audio element:", audio);
    console.log("🎵 Audio readyState:", audio?.readyState);
    console.log("🎵 Is valid URL:", isValidUrl);
    console.log("🎵 Audio error state:", audioError);

    if (!isValidUrl || audioError) {
      console.warn("⚠️ Cannot play: Invalid URL or audio error");
      return;
    }

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((error) => {
        console.error("❌ Error playing audio:", error);
        setErrorMessage("Failed to play audio");
        setAudioError(true);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const progressBar = progressRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = progressBar.clientWidth;

    // Validate duration before using it
    if (!duration || isNaN(duration) || !isFinite(duration) || duration <= 0) {
      console.warn("⚠️ Cannot seek: Invalid duration", duration);
      return;
    }

    const newTime = (clickX / width) * duration;

    // Validate the calculated time
    if (isNaN(newTime) || !isFinite(newTime) || newTime < 0) {
      console.warn("⚠️ Cannot seek: Invalid calculated time", newTime);
      return;
    }

    try {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    } catch (error) {
      console.error("❌ Error setting currentTime:", error);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === null || time === undefined || time < 0)
      return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const formatted =
      String(minutes).padStart(2, "0") + ":" + String(seconds).padStart(2, "0");
    console.log(`🎵 formatTime: ${time}s -> ${formatted}`);
    return formatted;
  };

  const displayName =
    audioDisplayName && typeof audioDisplayName === "string" && audioDisplayName.trim() !== ""
      ? audioDisplayName.trim()
      : null;

  // Still signing the storage link: say so instead of a dead Play button.
  if (isSigning && !errorMessage) {
    return (
      <div className="audio-player-signing" style={styles.audioPlayer} role="status" aria-live="polite">
        {displayName ? (
          <Text as="div" width="100%" textAlign="center" fontWeight="semibold" fontSize="md" color="inherit" mb={3} wordBreak="break-word">
            {displayName}
          </Text>
        ) : null}
        <Spinner size="md" thickness="3px" color={surf.info} mb={3} />
        <Text fontSize="sm" fontWeight="semibold" color="inherit">
          Creating secure link…
        </Text>
        <Text fontSize="xs" color={surf.gray.body} mt={1} textAlign="center">
          Fetching the recording from storage. This can take a few seconds.
        </Text>
      </div>
    );
  }

  // Show error message if URL is invalid or audio failed to load
  if (!isValidUrl || audioError || errorMessage) {
    const isSupabaseStorage =
      urlValidation?.details?.type === "supabase_storage";

    return (
      <div className="audio-player-error" style={styles.audioPlayer}>
        {audioDisplayName && typeof audioDisplayName === "string" && audioDisplayName.trim() !== "" ? (
          <Text
            as="div"
            width="100%"
            textAlign="center"
            fontWeight="semibold"
            fontSize="md"
            color="inherit"
            mb={3}
            wordBreak="break-word"
          >
            {audioDisplayName.trim()}
          </Text>
        ) : null}
        <Alert status="error" borderRadius="md" {...alertTone("red")}>
          <AlertIcon color={tone === "dark" ? surf.red.title : undefined} />
          <AlertTitle mr={2}>Audio Playback Error</AlertTitle>
          <AlertDescription>
            {errorMessage || "Unable to load audio file"}
          </AlertDescription>
        </Alert>

        {isSupabaseStorage &&
          accessibilityCheck &&
          errorMessage !== EMPTY_RECORDING_ERROR_MSG &&
          !accessibilityCheck.isAccessible && (
            <Alert status="warning" borderRadius="md" mt={3} {...alertTone("orange")}>
              <AlertIcon color={tone === "dark" ? surf.orange.title : undefined} />
              <AlertTitle mr={2}>Supabase Storage Issue</AlertTitle>
              <AlertDescription fontSize="sm">
                {accessibilityCheck.status === 400
                  ? "The audio file may be corrupted, in the wrong bucket, or access is denied."
                  : accessibilityCheck.status === 404
                  ? "The audio file could not be found in storage."
                  : accessibilityCheck.status === 403
                  ? "Access denied - you don't have permission to view this audio file."
                  : "There may be an issue with file permissions or storage configuration."}
                <br />
                Please contact support if this issue persists.
              </AlertDescription>
            </Alert>
          )}

        {/* Show file information */}
        {audioMetadata && (
          <Box mt={4} p={3} bg={surf.gray.bg} borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" color={surf.gray.title}>
              File Information:
            </Text>
            <Text fontSize="xs" color={surf.gray.body} mt={1}>
              Name: {audioMetadata.fileName}
              <br />
              Bucket: {audioMetadata.bucket}
              <br />
              {audioMetadata.filePath && (
                <>
                  Path: {audioMetadata.filePath}
                  <br />
                </>
              )}
              {audioMetadata.recordedDate &&
                `Recorded: ${audioMetadata.recordedDate}`}
            </Text>
          </Box>
        )}

        {/* Show accessibility check status */}
        {isCheckingAccessibility && (
          <Box mt={2} textAlign="center">
            <Text fontSize="xs" color={surf.info}>
              🔍 Checking file accessibility...
            </Text>
          </Box>
        )}

        {accessibilityCheck && (
          <Box mt={2} textAlign="center">
            <Text
              fontSize="xs"
              color={accessibilityCheck.isAccessible ? surf.ok : surf.bad}
            >
              {accessibilityCheck.isAccessible
                ? `✅ File accessible (${accessibilityCheck.status})`
                : `❌ ${accessibilityCheck.error || "File not accessible"}`}
            </Text>
          </Box>
        )}

        {backupUrl && (
          <Box mt={4} p={3} bg={surf.orange.bg} border="1px solid" borderColor={surf.orange.border} borderRadius="md" w="100%">
            <Text fontSize="sm" fontWeight="semibold" color={surf.orange.title} mb={1}>
              🔄 Last Valid Backup Available
            </Text>
            <Text fontSize="xs" color={surf.orange.body} mb={2}>
              The main file couldn't be played, but a valid backup recording was found.
              It may be slightly shorter than the original.
            </Text>
            <audio
              controls
              src={backupUrl}
              style={{ width: "100%", marginBottom: "0.5rem" }}
            />
            {replaceSuccess ? (
              <Box mt={2} p={2} bg={surf.green.bg} border="1px solid" borderColor={surf.green.border} borderRadius="md">
                <Text fontSize="xs" color={surf.green.title} fontWeight="semibold">
                  ✅ Backup successfully replaced the original file. Refresh the page to play it.
                </Text>
                <Button size="xs" colorScheme="green" mt={2} onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </Box>
            ) : (
              <Box mt={1} display="flex" gap={2} flexWrap="wrap">
                <Button
                  as="a"
                  href={backupUrl}
                  download="backup_recording.webm"
                  size="xs"
                  colorScheme="orange"
                  variant="outline"
                >
                  Download Backup
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={handleReplaceWithBackup}
                  isLoading={isReplacing}
                  loadingText="Replacing..."
                  isDisabled={isReplacing}
                >
                  Replace with Backup
                </Button>
              </Box>
            )}
            {replaceError && (
              <Text fontSize="xs" color={surf.red.body} mt={2}>
                ❌ {replaceError}
              </Text>
            )}
          </Box>
        )}

        {allBackupsFailed && !backupUrl && (
          <Box mt={4} p={3} bg={surf.red.bg} border="1px solid" borderColor={surf.red.border} borderRadius="md" w="100%">
            <Text fontSize="sm" fontWeight="semibold" color={surf.red.title} mb={1}>
              ❌ No Valid Backups Available
            </Text>
            <Text fontSize="xs" color={surf.red.body}>
              All backup recordings for this audio were also unplayable or could not be found.
              The recording session may not have been saved properly.
            </Text>
          </Box>
        )}

        {autoDeleteStatus === "deleting" && (
          <Box mt={3} p={2} bg={surf.orange.bg} border="1px solid" borderColor={surf.orange.border} borderRadius="md" w="100%">
            <Text fontSize="xs" color={surf.orange.title}>
              Cleaning up invalid backup file from storage...
            </Text>
          </Box>
        )}
        {autoDeleteStatus === "deleted" && (
          <Box mt={3} p={2} bg={surf.green.bg} border="1px solid" borderColor={surf.green.border} borderRadius="md" w="100%">
            <Text fontSize="xs" color={surf.green.title}>
              Invalid backup file was automatically deleted.
            </Text>
          </Box>
        )}
        {autoDeleteStatus === "failed" && (
          <Box mt={3} p={2} bg={surf.yellow.bg} border="1px solid" borderColor={surf.yellow.border} borderRadius="md" w="100%">
            <Text fontSize="xs" color={surf.yellow.title}>
              Could not auto-delete this invalid backup file. Please delete it manually from Audio Manager.
            </Text>
          </Box>
        )}
      </div>
    );
  }

  return (
    <div className="audio-player" style={styles.audioPlayer}>
      {audioDisplayName && typeof audioDisplayName === "string" && audioDisplayName.trim() !== "" ? (
        <Text
          as="div"
          width="100%"
          textAlign="center"
          fontWeight="semibold"
          fontSize="md"
          // `inherit`, not a fixed gray: this player renders both in the light
          // Chakra modals (where the inherited body color IS gray.800, so
          // nothing changes) and inside the dark-mode clinic Audios modal,
          // where a hardcoded gray.800 was invisible against the panel.
          color="inherit"
          mb={3}
          wordBreak="break-word"
        >
          {audioDisplayName.trim()}
        </Text>
      ) : null}
      <Button
        colorScheme="blue"
        onClick={togglePlayPause}
        style={styles.playPauseButton}
        isDisabled={!isValidUrl || audioError}
      >
        {isPlaying ? "Pause" : "Play"}
      </Button>
      <div
        className="progress-bar"
        ref={progressRef}
        style={styles.progressBar}
        onClick={handleProgressClick}
      >
        <div
          className="progress-filled"
          style={{
            ...styles.progressFilled,
            width: `${
              duration && isFinite(duration) && duration > 0
                ? (currentTime / duration) * 100
                : 0
            }%`,
          }}
        ></div>
      </div>
      <div className="time-display" style={styles.timeDisplay}>
        <span>{formatTime(currentTime)}</span> /{" "}
        <span>{formatTime(duration)}</span>
      </div>
      {/* Handlers are props, not addEventListener in an effect: the error view
          unmounts this element (e.g. a stored signed URL that expired fails
          before the fresh one resolves), and listeners attached to the old
          element never reached its replacement, so the bar stopped moving. */}
      <audio
        ref={audioRef}
        src={resolvedAudioUrl}
        preload="metadata"
        style={{ display: "none" }}
        onTimeUpdate={updateCurrentTime}
        onLoadedMetadata={updateLoadedMetadata}
        onDurationChange={updateLoadedMetadata}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        onLoadStart={() => {
          console.log("🎵 Audio load started");
          setAudioError(false);
        }}
        onCanPlay={() => {
          console.log("🎵 Audio can play");
          setAudioError(false);
        }}
        onLoadedData={() => {
          console.log("🎵 Audio data loaded");
        }}
      />
    </div>
  );
};

// Inline styles for simplicity
const styles = {
  audioPlayer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "500px",
    fontFamily: "Arial, sans-serif",
    padding: "20px",
  },
  audioPlayerError: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "500px",
    fontFamily: "Arial, sans-serif",
    padding: "20px",
  },
  playPauseButton: {
    padding: "10px 20px",
    marginBottom: "10px",
    border: "none",
    // backgroundColor: "#0073e6",
    color: "white",
    cursor: "pointer",
    borderRadius: "4px",
  },
  progressBar: {
    width: "100%",
    height: "10px",
    // Translucent neutral instead of #eee so the unplayed track reads as a
    // track on a dark surface too (it used to be a bright bar in the clinic
    // Audios modal).
    backgroundColor: "rgba(128, 128, 128, 0.3)",
    cursor: "pointer",
    position: "relative",
    borderRadius: "5px",
    overflow: "hidden",
  },
  progressFilled: {
    height: "100%",
    backgroundColor: "#0073e6",
    width: "0%",
  },
  timeDisplay: {
    marginTop: "10px",
    fontSize: "14px",
    // Same reasoning as the display name above — inherit the host's text
    // color, dimmed, rather than baking in a light-mode gray.
    color: "inherit",
    opacity: 0.75,
  },
};

export default AudioPlayer;
